import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Link, RouteComponentProps, withRouter } from 'react-router-dom'
import Button from '../../Components/Buttons'
import Footer from '../../Components/Footer'
import Planet from '../../Components/Planet/Planet'
import Progress from '../../Components/Progress/Progress'
import Ticker from '../../Components/Ticker/Ticker'
import { useIsAuthenticated } from '../../hooks/authenticated'
import { setLastAggregatePageVisited } from '../../redux/systemSlice'
import { Helmet } from 'react-helmet-async'
import './_Home.scss'
import { DOC_TEMPLATES } from '../../constants'
import useEnterApp from '../../hooks/enterApp'
import AcademicIntegrity from './AcademicIntegrity'
import ExamplesBg from '../../assets/images/paint.jpeg'
import ToolsSection from './Tools'
import { usePostHog } from 'posthog-js/react'
import useNewDocument from '../../hooks/useNewDocument'
import Loader from '../../Components/Loader'

function Home(props: RouteComponentProps) {
	const posthog = usePostHog()
	const dispatch = useDispatch()
	const pageName = 'home'
	const isAuthenticated = useIsAuthenticated()
	const { createDoc, loading, templateID } = useNewDocument()
	const { enterApp } = useEnterApp({ history: props.history })

	useEffect(() => {
		dispatch(setLastAggregatePageVisited({ value: '/' }))
		// update only on initial page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const circleContent = [
		<div className="flex flex-col gap-6">
			<h3 className="fancy-font text-7xl">
				<span className="bg-secondary">#1</span>
			</h3>
			<h5 className="text-3xl">Enter a topic</h5>
		</div>,
		<div className="flex flex-col gap-6">
			<h3 className="fancy-font text-7xl">
				<span className="bg-secondary">#2</span>
			</h3>
			<h5 className="text-3xl">
				<span>
					The Good <span className="logoSubText">AI</span>
				</span>{' '}
				will generate a high quality outline on this topic.
			</h5>
		</div>,
		<div className="flex flex-col gap-6">
			<h3 className="fancy-font text-7xl">
				<span className="bg-secondary">#3</span>
			</h3>
			<h5 className="text-3xl">Write each section outline generated.</h5>
		</div>,
		<div className="flex flex-col gap-6">
			<h3 className="fancy-font text-7xl">
				<span className="bg-secondary">#4</span>
			</h3>
			<h5 className="text-3xl">Export your essay and you're done!</h5>
		</div>,
	]

	function chooseTool(toolID: string) {
		if (isAuthenticated) {
			props.history.push('/new/' + toolID)
		} else if (!loading) {
			enterApp('/new/' + toolID)
		}
	}

	return (
		<>
			<Helmet>
				<title>AI Essay Writer | Free Essay Writer</title>
				<meta
					name="description"
					content="AI essay writer by The Good AI is an AI essay writer that writes your essays for you. Provide a title, word count, and it'll write a high quality essay with references. AI essay writer with no sign up required!"
				/>
				<meta
					name="keywords"
					content="ai essay writer, essay writer ai, ai essay, ai essay writing, ai essay writer free"
				/>
			</Helmet>
			<main id={pageName} className="overflow-hidden">
				<div className="px-4 lg:px-56 lg:pb-28 pb-16 lg:pt-40 pt-24 max-w-[1800px] mx-auto">
					<div className="flex flex-wrap items-stretch justify-between">
						<div className="w-full md:w-[calc(60%-60px)] pr-0 flex flex-col gap-6">
							<h2 className="text-6xl lg:text-8xl">
								<span className="fancy-font">Advanced AI</span> made easy
							</h2>
							<p className="mt-2 text-gray-600 leading-relaxed">
								Overcome writer's block, try our AI essay writer and assistant for free today! Trusted by over 3 million
								writers.
							</p>
						</div>
						<div className="w-full md:w-[calc(100%-60%)] ">
							<span
								onClick={() => {
									chooseTool(DOC_TEMPLATES.Writer)
									posthog.capture('template-chosen', { type: 'AI Essay Writer' })
								}}
								className="flex items-center justify-center border-dotted border border-primary h-16 lg:h-32 md:h-24 text-center text-primary cursor-pointer hover:bg-secondary hover:border-solid"
							>
								<div className="flex items-start justify-center gap-2">
									{loading && templateID === DOC_TEMPLATES.Writer && <Loader />}
									<h1 className="text-2xl lg:text-4xl">AI Essay Writer</h1>
								</div>
							</span>
							<span
								onClick={() => {
									chooseTool(DOC_TEMPLATES.Outline)
									posthog.capture('template-chosen', { type: 'AI Essay Outliner' })
								}}
								className="flex items-center justify-center border-dotted border border-primary h-16 lg:h-32 md:h-24 text-center text-primary cursor-pointer mt-6 hover:bg-secondary hover:border-solid"
							>
								<div className="flex items-start justify-center gap-2">
									{loading && templateID === DOC_TEMPLATES.Outline && <Loader />}
									<h1 className="text-2xl lg:text-4xl">AI Essay Outliner</h1>
								</div>
							</span>
						</div>
					</div>
				</div>
				<Ticker />
				<div className="px-4 lg:px-56 lg:pt-20 pt-24 max-w-[1800px] mx-auto">
					<div className="text-center pb-8">
						<h5 className="fancy-font text-md text-3xl mb-6">As Featured In</h5>
						<div className="flex flex-wrap justify-between items-center opacity-50 sm:flex-row flex-col gap-3">
							<img
								src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/Awwards-logotype-2018.svg/1280px-Awwards-logotype-2018.svg.png"
								alt="Awwwards logo"
								className="w-1/2 sm:w-1/5 my-2"
							/>
							<img
								src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/95/Wired_logo.svg/1280px-Wired_logo.svg.png"
								alt="Wired logo"
								className="w-1/2 sm:w-1/5 my-2"
							/>
							<img
								src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/Medium_logo_Wordmark_Black.svg/1280px-Medium_logo_Wordmark_Black.svg.png"
								alt="Medium logo"
								className="w-1/2 sm:w-1/5 my-2"
							/>
						</div>
					</div>
					<hr className="border-t border-b-0 border-dotted border-black" />
					<div className="py-20">
						<div className="flex flex-wrap-reverse justify-between text-center md:text-left">
							<h3 className="w-full md:w-3/5 md:text-7xl text-6xl md:leading-tight leading-snug">
								Write A+ essays <br />
								<span className="fancy-font highlighted">in less time.</span>
							</h3>
							<div className="w-full md:w-1/5 flex justify-center -z-10">
								<div>
									<div className="spinner-blur">
										<span></span>
									</div>
								</div>
							</div>
						</div>
						<div className="flex flex-wrap justify-between mt-12 lg:mt-8">
							<div className="w-full md:w-1/3 border p-5 bg-white border-primary">
								<span className="text-6xl bg-white float-right relative top-12 left-[50px] hidden md:block">⤑</span>
								<h5 className="text-3xl mb-2">Choose your needs</h5>
								<p className="text-gray-700 leading-relaxed">
									Type in any topic, essay type, essay tone, and whether to include references.
								</p>
							</div>
							<div className="w-full md:w-1/3 border p-5 pl-8 bg-white border-t-0 md:border-t md:border-b border-b-0 md:border-l-0 md:border-r-0 border-primary">
								<span className="text-6xl bg-white float-right relative top-12 left-[50px] hidden md:block z-20">
									⤑
								</span>
								<h5 className="text-3xl mb-2">Essay is written</h5>
								<p className="text-gray-700 leading-relaxed">
									A well-written, unique, and accurate essay is generated on the given topic to help you get started.
								</p>
							</div>
							<div className="w-full md:w-1/3 border p-5 pl-8 bg-white border-primary">
								<h5 className="text-3xl mb-2">Edit your essay</h5>
								<p className="text-gray-700 leading-relaxed">
									Use the essay as inspiration and utilize our AI editing tool and AI auto-complete features to complete
									your essay.
								</p>
							</div>
						</div>
					</div>
					<hr className="border-t border-b-0 border-dotted border-black" />
					<div className="flex flex-wrap justify-between py-20">
						<div className="w-full md:w-1/2 flex flex-col gap-4">
							<h3 className="md:text-6xl text-5xl md:leading-tight leading-snug">
								Trained on <span className="highlighted">millions</span> of{' '}
								<span className="fancy-font">high quality</span> essays
							</h3>
							<p className="text-gray-700 leading-relaxed">
								Our AI essay writer is trained on millions of high-quality essays, ensuring it is equipped with the
								knowledge to generate distinctive, top-tier essays.
							</p>
						</div>
						<div className="hidden md:block w-full md:w-1/2">
							<Planet />
						</div>
					</div>
					<hr className="border-t border-dotted border-black border-b-0" />
					<div className="flex flex-wrap justify-between py-10 lg:py-20">
						<Link
							to="/examples"
							className="w-full md:w-[calc(40%-20px)] sm:w-[calc(60%-20px)] m-auto md:m-0 rounded-t-full max-w-96"
						>
							<div className="relative cursor-pointer text-center group rounded-t-full">
								<img src={ExamplesBg} alt="paint swirls" className="w-full h-100 object-cover rounded-t-full" />
								<Button type="secondary" className="examplesBtn absolute text-2xl group-hover:bg-secondary bg-white">
									<span>View examples</span>
								</Button>
							</div>
						</Link>
						<div className="w-full md:w-[calc(60%-60px)] text-center md:text-right flex flex-col justify-start gap-6 mt-10 md:mt-0">
							<h4 className="md:text-8xl text-7xl leading-0 md:leading-0">
								Fast <span className="outlined">&</span> Accurate
							</h4>
							<p className="text-gray-700 leading-relaxed">
								Accelerate your writing process with our AI essay writer. Our AI essay writer helps overcome writer's
								block by generating an essay that contains accurate, well-researched information.
							</p>
						</div>
					</div>
					<hr className="border-t border-dotted border-black" />
					<ToolsSection />
					<hr className="border-t border-dotted border-black" />
					<div className="flex flex-wrap justify-between py-10 md:py-20" id="outline">
						<div className="w-full md:w-2/5">
							<h2 className="text-6xl md:text-7xl text-center md:text-left md:leading-tight leading-tight">
								Get ideas with our <span className="fancy-font highlighted">Essay Outliner</span>
							</h2>
						</div>
						<div className="w-full md:w-1/2 flex justify-center md:justify-end mt-10 md:mt-0 max-w-lg">
							<div className="relative w-full md:w-4/5 sm:w-3/5">
								<Progress content={circleContent} />
							</div>
						</div>
					</div>
					<hr className="border-t border-dotted border-black" />
					<AcademicIntegrity />
				</div>
				<Footer />
			</main>
		</>
	)
}

export default withRouter(Home)
